import { sanitizeFilterQueryValue } from '@/common/utils/sanitizeFilterQueryValue';
import type { ViewBox } from '../components/MapRenderer/helpers';

export type SortModel = {
  field: string;
  sort: 'asc' | 'desc';
  /** Geo center for the computed `distance` sort. */
  center?: { lat: number; lng: number };
};

/**
 * Formats a sort model into the `sort` / `distanceFrom` query params the
 * identity filter DSL expects. The computed `distance` sort needs its center
 * in a separate `distanceFrom=lng,lat` param (the legacy search plugin took
 * it as a `field:DIR:segment` suffix instead).
 */
export function buildSortParams(sortModel: SortModel[]): {
  sort?: string;
  distanceFrom?: string;
} {
  const sort =
    sortModel
      .map(({ field, sort }) => `${field}:${sort.toUpperCase()}`)
      .join(';') || undefined;
  const center = sortModel.find((model) => model.center)?.center;
  return {
    sort,
    distanceFrom: center ? `${center.lng},${center.lat}` : undefined,
  };
}

/**
 * Builds the `q` filter for `GET /teams/locations` from the parts the partner
 * map filters on (free text, then viewBox, status), joined with the DSL's `&`.
 * The legacy `offboarding` boolean is gone — OFFBOARDING is a lifecycle
 * status now, so `status[eq]=ACTIVE` already excludes offboarding teams.
 */
export function buildTeamLocationsQuery({
  search,
  viewBox,
  status,
}: {
  search?: string;
  viewBox?: ViewBox;
  status?: string;
}): string | undefined {
  const parts: string[] = [];

  if (search) {
    parts.push(sanitizeFilterQueryValue(search));
  }
  if (viewBox) {
    const { lngMin, latMin, lngMax, latMax } = viewBox;
    parts.push(
      `mailingAddress.viewBox[in]=[${lngMin},${latMin},${lngMax},${latMax}]`,
    );
  }
  if (status) {
    parts.push(`status[eq]=${status}`);
  }

  return parts.length ? parts.join('&') : undefined;
}
